/**
 * @fileoverview API functions for fetching Kick.com data using Puppeteer to bypass Cloudflare
 * @version 1.0.0
 * @author Kick Flow Team
 */

import puppeteer from 'puppeteer-extra';
import StealthPlugin from 'puppeteer-extra-plugin-stealth';
import { KickChannelInfo } from '../types/channels';

/**
 * Base URL for Kick.com API
 */
const KICK_API_BASE = 'https://kick.com/api/v2';

/**
 * Setup Puppeteer with Stealth plugin to bypass Cloudflare protection
 */
const setupPuppeteer = async () => {
  const puppeteerExtra = puppeteer.use(StealthPlugin());
  const browser = await puppeteerExtra.launch({
    headless: true,
    defaultViewport: null,
    args: ['--start-maximized', '--no-sandbox', '--disable-setuid-sandbox'],
  });
  const page = await browser.newPage();
  return { browser, page };
};

/**
 * Fetches channel data from Kick.com API using Puppeteer to bypass Cloudflare
 * @param channelName - The channel name/slug
 * @returns Promise with channel information including chatroom ID
 */
export async function getChannelData(channelName: string): Promise<KickChannelInfo> {
  const { browser, page } = await setupPuppeteer();

  try {
    const response = await page.goto(`${KICK_API_BASE}/channels/${channelName}`);

    if (response?.status() === 403) {
      throw new Error(
        'Request blocked by Cloudflare protection. Please try again later.'
      );
    }

    await page.waitForSelector('body');

    const jsonContent: KickChannelInfo = await page.evaluate(() => {
      const bodyElement = document.querySelector('body');
      if (!bodyElement || !bodyElement.textContent) {
        throw new Error('Unable to fetch channel data');
      }
      return JSON.parse(bodyElement.textContent);
    });

    return jsonContent;
  } catch (error) {
    console.error('Error getting channel data:', error);
    throw new Error(`Failed to fetch channel data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  } finally {
    await browser.close();
  }
}

/**
 * Get chatroom ID from channel name using Puppeteer
 * @param channelName - The channel name (slug)
 * @returns The chatroom ID for WebSocket connection, or null if not found
 */
export async function getChatroomId(channelName: string): Promise<number | null> {
  try {
    const channelData = await getChannelData(channelName);
    return channelData?.chatroom?.id || null;
  } catch (error) {
    console.error(`Failed to get chatroom ID for channel "${channelName}":`, error);
    return null;
  }
}
